import { Table } from 'app/shared/components/generic-ui/Table/Table';
import { useAuthClient } from 'app/shared/hooks/useAuthClient';
import { useError } from 'app/shared/providers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import {
  Button as MuiButton,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Paper,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Search } from 'app/shared/components/generic-ui/Search/Search';
import {
  CellAlignment,
  CellSize,
} from 'app/shared/components/generic-ui/Table/Table.types';
import { translations } from 'app/locales/i18n';
import { IError } from 'types';
import { useTranslation } from 'react-i18next';
import { useStyles } from './VehicleDevices.styles';
import {
  ActionButton,
  ActionButtonStyle,
  IAction,
} from 'app/shared/components/generic-ui/ActionButton/ActionButton';

interface Device {
  deviceId?: string;
  vehicleDeviceId?: string;
  vehicleDeviceConfigurationId?: string;
  vehicleId?: string;
  deviceType?: string;
  gaugePos?: string;
  slopePerKmh?: number;
  offsetKg?: number;
  strainScale?: number;
  active?: boolean;
  __isNew?: boolean;
  [key: string]: unknown;
}

export const VehicleDevices = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { organizationId } = useParams<{ organizationId: string }>();
  const authClient = useAuthClient();
  const authClientRef = useRef(authClient);
  authClientRef.current = authClient;
  const { openModal } = useError();
  const openModalRef = useRef(openModal);
  openModalRef.current = openModal;
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editingDeviceId, setEditingDeviceId] = useState<string>();
  const [editValues, setEditValues] = useState<Partial<Device>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState<string[]>([]);
  const [gaugePositions, setGaugePositions] = useState<string[]>([]);
  const [vehicleIds, setVehicleIds] = useState<string[]>([]);
  const [vehicleSearchOpen, setVehicleSearchOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    authClientRef
      .current(`/api/v1/${organizationId}/VehicleDeviceConfiguration`, {
        method: 'GET',
      })
      .then(response => {
        if (isMounted) {
          setDevices(
            (response?.data || []).map((device: Device) => {
              const values = device as Record<string, unknown>;
              const deviceIdEntry = Object.entries(values).find(
                ([key]) => key.toLowerCase() === 'deviceid',
              );

              return {
                ...device,
                deviceId: device.deviceId ?? (deviceIdEntry?.[1] as string),
              };
            }),
          );
        }
      })
      .catch(error => openModalRef.current(error))
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [organizationId]);

  useEffect(() => {
    let isMounted = true;
    Promise.all([
      authClientRef.current(
        `/api/v1/${organizationId}/VehicleDeviceConfiguration/device-types`,
        { method: 'GET' },
      ),
      authClientRef.current(
        `/api/v1/${organizationId}/VehicleDeviceConfiguration/gauge-positions`,
        { method: 'GET' },
      ),
    ])
      .then(([typesResponse, positionsResponse]) => {
        if (isMounted) {
          setDeviceTypes(typesResponse?.data || []);
          setGaugePositions(positionsResponse?.data || []);
        }
      })
      .catch(error => openModalRef.current(error));

    return () => {
      isMounted = false;
    };
  }, [organizationId]);

  const fetchVehicleIds = useCallback(
    async (query = '') => {
      const response = await authClientRef.current(
        `/api/v1/${organizationId}/NotificationEntity/4/get-notification-entities`,
        {
          method: 'GET',
          queryParameters: { query, take: 50 },
        },
      );
      setVehicleIds(
        (response?.data || [])
          .map((vehicle: { externalId?: string }) => vehicle.externalId)
          .filter((externalId: string | undefined): externalId is string =>
            Boolean(externalId),
          ),
      );
    },
    [organizationId],
  );

  useEffect(() => {
    fetchVehicleIds().catch(error => openModalRef.current(error));
  }, [fetchVehicleIds]);

  useEffect(() => {
    if (!editingDeviceId && !isAdding) return;
    const query = String(editValues.vehicleId ?? '');
    fetchVehicleIds(query).catch(error => openModalRef.current(error));
  }, [editValues.vehicleId, editingDeviceId, fetchVehicleIds, isAdding]);

  const filteredDevices = useMemo(() => {
    const matches = (value: unknown) =>
      String(value ?? '')
        .toLowerCase()
        .includes(search.toLowerCase());

    return devices.filter(device => {
      const deviceRecord = device as Record<string, unknown>;
      const deviceId = Object.entries(deviceRecord).find(
        ([key]) => key.toLowerCase() === 'deviceid',
      )?.[1];

      return matches(deviceId) || matches(device.vehicleId);
    });
  }, [devices, search]);

  useEffect(() => {
    if (!isAdding && !editingDeviceId) setVehicleSearchOpen(false);
  }, [editingDeviceId, isAdding]);

  const saveDevice = useCallback(
    async (device: Device) => {
      try {
        const updatedDevice = {
          ...device,
          ...editValues,
          ...(editValues.active !== undefined
            ? {
                active: Boolean(editValues.active),
              }
            : {}),
        };
        const deviceGuid =
          device.vehicleDeviceConfigurationId ||
          device.vehicleDeviceId ||
          device.deviceId;
        await authClient(
          `/api/v1/${organizationId}/VehicleDeviceConfiguration/${deviceGuid}`,
          {
            method: 'PUT',
            data: updatedDevice,
          },
        );
        setDevices(current =>
          current.map(item =>
            item.deviceId === device.deviceId ? updatedDevice : item,
          ),
        );
        await fetchVehicleIds();
        setEditingDeviceId(undefined);
        setEditValues({});
      } catch (error) {
        openModal(error as IError);
      }
    },
    [authClient, editValues, fetchVehicleIds, openModal, organizationId],
  );

  const deleteDevice = useCallback(
    async (device: Device) => {
      try {
        await authClient(
          `/api/v1/${organizationId}/VehicleDeviceConfiguration/${
            device.vehicleDeviceConfigurationId ||
            device.vehicleDeviceId ||
            device.deviceId
          }`,
          { method: 'DELETE' },
        );
        setDevices(current =>
          current.filter(item => item.deviceId !== device.deviceId),
        );
      } catch (error) {
        openModal(error as IError);
      }
    },
    [authClient, openModal, organizationId],
  );

  const addDevice = useCallback(async () => {
    try {
      await fetchVehicleIds();
      const response = await authClient(
        `/api/v1/${organizationId}/VehicleDeviceConfiguration`,
        { method: 'POST', data: editValues },
      );
      setDevices(current => [
        ...current.filter(device => !device.__isNew),
        response?.data,
      ]);
      setEditValues({});
      setIsAdding(false);
    } catch (error) {
      openModal(error as IError);
    }
  }, [authClient, editValues, fetchVehicleIds, openModal, organizationId]);

  const renderCell = useCallback(
    (row: Device, cellId: string) => {
      const currentValue = String(editValues[cellId] ?? row[cellId] ?? '');
      const updateValue = (value: unknown) =>
        setEditValues(current => ({ ...current, [cellId]: value }));

      if (row.__isNew && cellId === 'active') {
        return null;
      }
      if (!row.__isNew && editingDeviceId !== row.deviceId) {
        return row[cellId] ?? '-';
      }
      if (cellId === 'vehicleId') {
        return (
          <div className={classes.vehicleInputContainer}>
            <TextField
              value={currentValue}
              onChange={event => {
                updateValue(event.target.value);
                setVehicleSearchOpen(true);
              }}
              onFocus={() => setVehicleSearchOpen(true)}
              size="small"
              slotProps={{
                input: { style: { color: '#000000' } },
                htmlInput: { style: { color: '#000000' } },
              }}
            />
            {vehicleSearchOpen && vehicleIds.length > 0 && (
              <Paper className={classes.vehicleOptions}>
                {vehicleIds.map(vehicleId => (
                  <div
                    key={vehicleId}
                    className={classes.vehicleOption}
                    onMouseDown={() => {
                      updateValue(vehicleId);
                      setVehicleSearchOpen(false);
                    }}
                  >
                    {vehicleId}
                  </div>
                ))}
              </Paper>
            )}
          </div>
        );
      }
      if (
        cellId === 'deviceType' ||
        cellId === 'gaugePos' ||
        cellId === 'active'
      ) {
        const options =
          cellId === 'deviceType'
            ? deviceTypes
            : cellId === 'gaugePos'
            ? gaugePositions
            : ['true', 'false'];
        return (
          <Select
            value={currentValue}
            onChange={event => updateValue(event.target.value)}
            className={classes.dropdown}
            inputProps={{ className: classes.dropdown }}
          >
            {options.map(option => (
              <MenuItem
                key={option}
                value={option}
                className={classes.dropdownOption}
              >
                {cellId === 'active'
                  ? option === 'true'
                    ? 'Active'
                    : 'Inactive'
                  : option}
              </MenuItem>
            ))}
          </Select>
        );
      }
      return (
        <TextField
          value={currentValue}
          onChange={event => updateValue(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') saveDevice(row);
          }}
          size="small"
          slotProps={{
            input: { style: { color: '#000000' } },
            htmlInput: { style: { color: '#000000' } },
          }}
        />
      );
    },
    [
      classes.vehicleInputContainer,
      classes.vehicleOption,
      classes.vehicleOptions,
      deviceTypes,
      editValues,
      vehicleIds,
      editingDeviceId,
      gaugePositions,
      vehicleSearchOpen,
      classes.dropdown,
      classes.dropdownOption,
      saveDevice,
    ],
  );

  const actions = useMemo<IAction[]>(
    () => [
      {
        label: 'Edit',
        onClick: row => {
          if (editingDeviceId || isAdding) return;
          setEditingDeviceId(row.deviceId);
          setEditValues({});
        },
      },
      { label: 'Delete', onClick: deleteDevice },
    ],
    [deleteDevice, editingDeviceId, isAdding],
  );

  const renderActionButton = useCallback(
    (row: Device) => {
      const isEditing = editingDeviceId === row.deviceId;
      const isNew = row.__isNew;
      if ((isAdding && !isNew) || (editingDeviceId && !isEditing)) {
        return <span />;
      }
      return isEditing || isNew ? (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <IconButton onClick={() => (isNew ? addDevice() : saveDevice(row))}>
            <CheckIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              setEditingDeviceId(undefined);
              setEditValues({});
              if (isNew) setIsAdding(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </span>
      ) : (
        <ActionButton
          style={ActionButtonStyle.LIGHT}
          options={actions}
          rowData={row}
        />
      );
    },
    [actions, addDevice, editingDeviceId, isAdding, saveDevice],
  );

  const columns = useMemo(
    () => [
      {
        id: 'deviceId',
        label: t(translations.devices.id),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'vehicleId',
        label: t(translations.devices.vehicleId),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'deviceType',
        label: t(translations.devices.deviceType),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'gaugePos',
        label: t(translations.devices.gaugePosition),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'slopePerKmh',
        label: t(translations.devices.slope),
        alignment: CellAlignment.RIGHT,
        size: CellSize.SMALL,
        sortable: false,
      },
      {
        id: 'offsetKg',
        label: t(translations.devices.offset),
        alignment: CellAlignment.RIGHT,
        size: CellSize.SMALL,
        sortable: false,
      },
      {
        id: 'strainScale',
        label: t(translations.devices.strainScale),
        alignment: CellAlignment.RIGHT,
        size: CellSize.SMALL,
        sortable: false,
      },
      {
        id: 'active',
        label: t(translations.devices.active),
        alignment: CellAlignment.RIGHT,
        size: CellSize.SMALL,
        sortable: false,
      },
    ],
    [t],
  );

  return (
    <div>
      <div className={classes.search}>
        <Search
          placeholder={t(translations.devices.search)}
          onChange={setSearch}
          onValueChange={setSearch}
          globalSearch={false}
        />
      </div>
      <Table
        columns={columns}
        tableData={
          isAdding ? [...filteredDevices, { __isNew: true }] : filteredDevices
        }
        withCheckbox={false}
        isLoading={isLoading}
        emptyTableText={t(translations.devices.empty)}
        renderCell={(row, cellId) =>
          cellId === 'active' && editingDeviceId !== row.deviceId
            ? row[cellId]
              ? 'Active'
              : 'Inactive'
            : renderCell(row, cellId)
        }
        actions={actions}
        renderActionButton={renderActionButton}
        renderActions={() => true}
      />
      <MuiButton
        className={classes.addButton}
        variant="contained"
        color="primary"
        disabled={isAdding || !!editingDeviceId}
        onClick={() => {
          setIsAdding(true);
          setEditValues({});
        }}
      >
        Add Vehicle Device
      </MuiButton>
    </div>
  );
};
